import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Typography, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import UsersCombo from './UsersCombo';
import Button from 'components/CustomButtons/Button.js';
import { colors } from './Theme/WebTheme';
import { FONT_FAMILY, WEB_MAIN_COLOR, formatUserAmount } from '../common/sharedFunctions';

const COP_PRESETS = ['50000', '100000', '120000', '200000', '500000'];

export default function AdminWalletCreditForm(props) {
    const {
        preselectedUid = null,
        defaultFilter = 'driver',
        compact = false,
        onSuccess,
    } = props;

    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const dispatch = useDispatch();
    const settings = useSelector((state) => state.settingsdata.settings);
    const userdata = useSelector((state) => state.usersdata);
    const auth = useSelector((state) => state.auth);
    const { addToWallet, fetchUsersOnce, parseNumberInput, formatNumberInput } = api;

    const [userFilter, setUserFilter] = useState(defaultFilter);
    const [users, setUsers] = useState([]);
    const [userCombo, setUserCombo] = useState(null);
    const [amount, setAmount] = useState(0);
    const [displayAmount, setDisplayAmount] = useState('');
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const canEdit = settings?.AllowCriticalEditsAdmin && auth?.profile?.usertype === 'admin';

    const buildUserOption = (user) => ({
        firstName: user.firstName,
        lastName: user.lastName,
        mobile: user.mobile,
        email: user.email,
        uid: user.id,
        usertype: user.usertype,
        walletBalance: user.walletBalance,
        desc: `${user.firstName} ${user.lastName} (${settings.AllowCriticalEditsAdmin ? user.mobile : t('hidden_demo')}) · ${formatUserAmount(user.walletBalance, settings)}`,
    });

    useEffect(() => {
        if (!userdata.users) return;
        let list = userdata.users;
        if (userFilter === 'driver') {
            list = list.filter((user) => user.usertype === 'driver');
        } else if (userFilter === 'customer') {
            list = list.filter((user) => user.usertype === 'customer');
        }
        setUsers(list.map(buildUserOption));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userdata.users, settings, userFilter, t]);

    useEffect(() => {
        if (!preselectedUid || !users.length) return;
        const match = users.find((user) => user.uid === preselectedUid);
        if (match) setUserCombo(match);
    }, [preselectedUid, users]);

    const selectedBalance = useMemo(() => {
        if (!userCombo) return null;
        const live = userdata.users?.find((user) => user.id === userCombo.uid);
        return live ? live.walletBalance : userCombo.walletBalance;
    }, [userCombo, userdata.users]);

    const handleAmountChange = (event) => {
        const { value } = event.target;
        const isVietnamese = settings && settings.country === 'Vietnam';

        if (isVietnamese) {
            const numericValue = parseNumberInput(value, true);
            setAmount(numericValue);
            setDisplayAmount(formatNumberInput(numericValue, true));
        } else {
            const numericValue = value === '' ? 0 : parseFloat(value);
            setAmount(Number.isFinite(numericValue) ? numericValue : 0);
            setDisplayAmount(value);
        }
    };

    const applyPreset = (preset) => {
        setAmount(parseFloat(preset));
        setDisplayAmount(preset);
    };

    const handleSubmit = async () => {
        if (!canEdit) {
            onSuccess?.({ ok: false, msg: t('demo_mode') });
            return;
        }
        if (!userCombo?.uid || !(amount > 0)) {
            onSuccess?.({ ok: false, msg: t('no_details_error') });
            return;
        }

        setSubmitting(true);
        const result = await dispatch(addToWallet(userCombo.uid, amount, note.trim() || 'AdminCredit'));
        setSubmitting(false);

        if (result?.success) {
            dispatch(fetchUsersOnce());
            setAmount(0);
            setDisplayAmount('');
            setNote('');
            onSuccess?.({
                ok: true,
                msg: t('admin_wallet_credit_success', { defaultValue: 'Capital acreditado correctamente en Movex' }),
                walletBalance: result.walletBalance,
            });
        } else {
            onSuccess?.({
                ok: false,
                msg: t('error', { defaultValue: 'Error al acreditar saldo' }),
            });
        }
    };

    if (!canEdit) {
        return (
            <Typography style={{ fontFamily: FONT_FAMILY, color: colors.CARD_LABEL, padding: 8 }}>
                {t('demo_mode')}
            </Typography>
        );
    }

    return (
        <Grid container spacing={2}>
            {!preselectedUid ? (
                <Grid item xs={12}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={userFilter}
                        onChange={(e, value) => value && setUserFilter(value)}
                        style={{ marginBottom: 8 }}
                    >
                        <ToggleButton value="driver">
                            {t('admin_wallet_driver_only', { defaultValue: 'Conductores' })}
                        </ToggleButton>
                        <ToggleButton value="customer">
                            {t('admin_wallet_customer_only', { defaultValue: 'Clientes' })}
                        </ToggleButton>
                        <ToggleButton value="all">
                            {t('all', { defaultValue: 'Todos' })}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Grid>
            ) : null}

            {!preselectedUid ? (
                <Grid item xs={12}>
                    {users.length ? (
                        <UsersCombo
                            placeholder={t('admin_wallet_select_driver', { defaultValue: 'Seleccionar conductor' })}
                            users={users}
                            value={userCombo}
                            onChange={(event, newValue) => setUserCombo(newValue)}
                        />
                    ) : null}
                </Grid>
            ) : userCombo ? (
                <Grid item xs={12}>
                    <Typography style={{ fontFamily: FONT_FAMILY, fontWeight: 'bold' }}>
                        {userCombo.firstName} {userCombo.lastName}
                    </Typography>
                </Grid>
            ) : null}

            {userCombo ? (
                <Grid item xs={12}>
                    <Typography style={{ fontFamily: FONT_FAMILY, color: colors.CARD_LABEL }}>
                        {t('admin_wallet_current_balance', { defaultValue: 'Capital actual' })}:{' '}
                        <strong>{formatUserAmount(selectedBalance, settings)}</strong>
                    </Typography>
                </Grid>
            ) : null}

            <Grid item xs={12} sm={compact ? 12 : 6}>
                <TextField
                    label={t('admin_wallet_credit_amount', { defaultValue: 'Capital a acreditar' })}
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={displayAmount}
                    onChange={handleAmountChange}
                    InputProps={{ style: { fontFamily: FONT_FAMILY } }}
                />
            </Grid>

            {!compact ? (
                <Grid item xs={12}>
                    <Typography variant="body2" style={{ fontFamily: FONT_FAMILY, marginBottom: 6 }}>
                        {t('quick_amount', { defaultValue: 'Montos rápidos' })}
                    </Typography>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {COP_PRESETS.map((preset) => (
                            <Button
                                key={preset}
                                size="sm"
                                onClick={() => applyPreset(preset)}
                                style={{
                                    backgroundColor: colors.THIRDCOLOR,
                                    color: colors.BLACK,
                                    fontFamily: FONT_FAMILY,
                                }}
                            >
                                {formatUserAmount(preset, settings)}
                            </Button>
                        ))}
                    </div>
                </Grid>
            ) : null}

            <Grid item xs={12} sm={compact ? 12 : 6}>
                <TextField
                    label={t('admin_wallet_credit_note', { defaultValue: 'Motivo (opcional)' })}
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    InputProps={{ style: { fontFamily: FONT_FAMILY } }}
                />
            </Grid>

            <Grid item xs={12} sm={compact ? 12 : 6}>
                <Button
                    size="lg"
                    onClick={handleSubmit}
                    disabled={submitting || !userCombo?.uid || !(amount > 0)}
                    style={{
                        width: '100%',
                        height: 40,
                        borderRadius: 30,
                        backgroundColor: WEB_MAIN_COLOR(settings?.appCat),
                        color: colors.WHITE,
                        fontWeight: 'bold',
                        fontFamily: FONT_FAMILY,
                        marginTop: compact ? 0 : 15,
                    }}
                >
                    {submitting
                        ? t('loading', { defaultValue: 'Cargando...' })
                        : t('add_to_wallet')}
                </Button>
            </Grid>
        </Grid>
    );
}
